import { U8, F32 } from '../engine/typed-arrays.js';

const ROW_LENGTH = 32;
function chooseTextureSize(length) {
  const width = 4096;
  const height = Math.max(1, Math.ceil(length / width));
  return { width, height };
}
function buildSplatGeometry(splatBuffer) {
  const u = new U8(splatBuffer);
  const f = new F32(splatBuffer);
  const vertexCount = u.byteLength / ROW_LENGTH | 0;
  if (vertexCount === 0) {
    throw new Error("splat buffer is empty");
  }
  const { width, height } = chooseTextureSize(vertexCount);
  const texelCount = width * height;
  const positions = new F32(vertexCount * 3);
  const centersRGBA = new F32(texelCount * 4);
  const covARGBA = new F32(texelCount * 4);
  const covBRGBA = new F32(texelCount * 4);
  const colorsRGBA = new F32(texelCount * 4);
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  const M = new F32(9);
  for (let i = 0; i < vertexCount; i++) {
    const fi = i * 8;
    const ui = i * ROW_LENGTH;
    const x = f[fi];
    const y = -f[fi + 1];
    const z = f[fi + 2];
    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    if (x < minX) {
      minX = x;
    }
    if (y < minY) {
      minY = y;
    }
    if (z < minZ) {
      minZ = z;
    }
    if (x > maxX) {
      maxX = x;
    }
    if (y > maxY) {
      maxY = y;
    }
    if (z > maxZ) {
      maxZ = z;
    }
    centersRGBA[i * 4] = x;
    centersRGBA[i * 4 + 1] = y;
    centersRGBA[i * 4 + 2] = z;
    centersRGBA[i * 4 + 3] = 1;
    colorsRGBA[i * 4] = u[ui + 24] / 255;
    colorsRGBA[i * 4 + 1] = u[ui + 25] / 255;
    colorsRGBA[i * 4 + 2] = u[ui + 26] / 255;
    colorsRGBA[i * 4 + 3] = u[ui + 27] / 255;
    let qw = -(u[ui + 28] - 127.5) / 127.5;
    let qx = (u[ui + 29] - 127.5) / 127.5;
    let qy = -(u[ui + 30] - 127.5) / 127.5;
    let qz = (u[ui + 31] - 127.5) / 127.5;
    const qLen = Math.hypot(qw, qx, qy, qz) || 1;
    const qInv = 1 / qLen;
    qw *= qInv;
    qx *= qInv;
    qy *= qInv;
    qz *= qInv;
    const sx = f[fi + 3] * 2;
    const sy = f[fi + 4] * 2;
    const sz = f[fi + 5] * 2;
    const xx = qx * qx, yy = qy * qy, zz = qz * qz;
    const xy = qx * qy, xz = qx * qz, yz = qy * qz;
    const wx = qw * qx, wy = qw * qy, wz = qw * qz;
    const r00 = 1 - 2 * (yy + zz);
    const r01 = 2 * (xy + wz);
    const r02 = 2 * (xz - wy);
    const r10 = 2 * (xy - wz);
    const r11 = 1 - 2 * (xx + zz);
    const r12 = 2 * (yz + wx);
    const r20 = 2 * (xz + wy);
    const r21 = 2 * (yz - wx);
    const r22 = 1 - 2 * (xx + yy);
    M[0] = r00 * sx;
    M[1] = r01 * sx;
    M[2] = r02 * sx;
    M[3] = r10 * sy;
    M[4] = r11 * sy;
    M[5] = r12 * sy;
    M[6] = r20 * sz;
    M[7] = r21 * sz;
    M[8] = r22 * sz;
    const a0 = M[0] * M[0] + M[3] * M[3] + M[6] * M[6];
    const a1 = M[0] * M[1] + M[3] * M[4] + M[6] * M[7];
    const a2 = M[0] * M[2] + M[3] * M[5] + M[6] * M[8];
    const b0 = M[1] * M[1] + M[4] * M[4] + M[7] * M[7];
    const b1 = M[1] * M[2] + M[4] * M[5] + M[7] * M[8];
    const b2 = M[2] * M[2] + M[5] * M[5] + M[8] * M[8];
    covARGBA[i * 4] = a0;
    covARGBA[i * 4 + 1] = a1;
    covARGBA[i * 4 + 2] = a2;
    covARGBA[i * 4 + 3] = 1;
    covBRGBA[i * 4] = b0;
    covBRGBA[i * 4 + 1] = b1;
    covBRGBA[i * 4 + 2] = b2;
    covBRGBA[i * 4 + 3] = 1;
  }
  return {
    vertexCount,
    boundMin: [minX, minY, minZ],
    boundMax: [maxX, maxY, maxZ],
    textureWidth: width,
    textureHeight: height,
    positions,
    centersRGBA,
    covARGBA,
    covBRGBA,
    colorsRGBA
  };
}

export { buildSplatGeometry };
//# sourceMappingURL=splat-data.js.map
